import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Box, Collapse, LinearProgress, Radio, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { getHours, getMinutes, isFuture, isValid, setDay } from "date-fns";
import { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import ButtonTabs from "@/components/ButtonTabs.tsx";
import DaysOfWeekPicker from "@/components/DaysOfWeekPicker";
import ConfirmDiscardChanges from "@/components/ModalForms/ConfirmDiscardChanges.tsx";
import ScheduleDateTimePicker from "@/components/Scheduling/ScheduleDateTimePicker";
import SelectRecipients from "@/components/SelectRecipients.tsx";
import TextMessageInput from "@/components/TextMessageInput.tsx";
import { FullInput } from "@/components/Utils.tsx";
import { calculateScheduledDateOrCron, modalTabs, roundTimeToNearest15Minutes } from "@/components/workflowUtils";
import { type BroadcastRecipientList } from "@/db/types.ts";
import { createLogger } from "@/logger";
import { createScheduledWorkflowItem, createWorkflowItem } from "@/models/actions/clientActions.ts";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { parseCronScheduledDate } from "@/utils";
import { useScheduledMessageTusUpload } from "./useScheduledMessageTusUpload";

const log = createLogger("feed");

const boxHeight = "370px";
const internalNameCharLimit = 40;

type Props = {
  askToConfirmClose?: boolean;
  closeModal: (value?: boolean) => void;
  setHasChanges: (value: boolean) => void;
};

export default function ScheduledMessageCreateModal({ askToConfirmClose, closeModal, setHasChanges }: Props) {
  const theme = useTheme();
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [activeTab, setActiveTab] = useState<string | null>("media");
  const [displayName, setDisplayName] = useState("");
  const [scheduledText, setScheduledText] = useState("");
  const [textValid, setTextValid] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [date, setDate] = useState<Date | null>(roundTimeToNearest15Minutes(new Date()));
  const [doesRepeat, setDoesRepeat] = useState(false);
  const [dayOfWeek, setDayOfWeek] = useState<number | null>(null);
  const [invalidTime, setInvalidTime] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const {
    uploadFile,
    isUploading,
    error: tusError,
    progressFraction,
    reset: resetTus,
  } = useScheduledMessageTusUpload();

  const [recipients, setRecipients] = useState<BroadcastRecipientList[]>([]);

  const hasValidSchedule =
    date != null &&
    isValid(date) &&
    !invalidTime &&
    (doesRepeat ? typeof dayOfWeek === "number" && !Number.isNaN(dayOfWeek) : true);

  const hasFutureSchedule = useMemo(() => {
    if (date == null || !isValid(date) || invalidTime) return false;
    if (doesRepeat) {
      if (typeof dayOfWeek !== "number" || Number.isNaN(dayOfWeek)) return false;
      const mins = getMinutes(date);
      const hours = getHours(date);
      const cronStr = `${mins} ${hours} ? * ${dayOfWeek} *`;
      const next = parseCronScheduledDate(cronStr, timezone);
      return next != null && isFuture(next);
    }
    return isFuture(date);
  }, [date, doesRepeat, dayOfWeek, invalidTime, timezone]);

  const hasRecipients = recipients.some((r) => Boolean(r?.id));
  const hasFile = !!selectedFile;
  const hasText = scheduledText.length > 0;
  /** File or TTS only (mutually exclusive in UI); TTS must pass validation. */
  const hasContent = (hasFile && !hasText) || (hasText && !hasFile && textValid);
  const hasInternalName = displayName.trim().length > 0;
  const canPublish = hasValidSchedule && hasFutureSchedule && hasRecipients && hasContent && hasInternalName;

  const hasDateChange = date !== null;
  const hasRecipientChanges = hasRecipients;
  const hasChangedValues = useMemo(() => {
    return [
      { key: "hasScheduledText", value: hasText },
      { key: "hasDisplayName", value: displayName.length > 0 },
      { key: "hasFile", value: hasFile },
      { key: "hasDateChange", value: hasDateChange },
      { key: "hasDoesRepeat", value: doesRepeat },
      { key: "hasRecipientChanges", value: hasRecipientChanges },
    ].filter((i) => i.value);
  }, [hasText, displayName.length, hasFile, hasDateChange, doesRepeat, hasRecipientChanges]);

  const hasChanges = hasChangedValues.length > 0;

  useEffect(() => {
    setHasChanges(hasChanges);
  }, [hasChanges, setHasChanges]);

  useEffect(() => {
    if (askToConfirmClose === true) {
      setConfirmDiscard(true);
    }
  }, [askToConfirmClose]);

  const maxDisplayNameHelper = `Max ${internalNameCharLimit.toLocaleString()} characters. (${
    displayName?.length ?? 0
  }/${internalNameCharLimit})`;

  const resetValues = () => {
    closeModal(true);
    setDisplayName("");
    setScheduledText("");
    setSelectedFile(null);
    resetTus();
    setDoesRepeat(false);
    setDayOfWeek(null);
    setDate(roundTimeToNearest15Minutes(new Date()));
    setInvalidTime(false);
    setRecipients([]);
    setDisabled(false);
    setActiveTab("media");
    setSubmitError(null);
  };

  const save = async () => {
    if (!date || !canPublish || !hasChanges) return;
    setDisabled(true);
    setSubmitError(null);
    try {
      let contentId = "";
      if (hasFile) {
        const r = await uploadFile(selectedFile!, displayName);
        contentId = r.contentId;
      }

      const workflowItem = await createWorkflowItem(
        workspaceId,
        contentId || undefined,
        displayName,
        hasText ? scheduledText : undefined,
      );

      const scheduledDateOrCron = calculateScheduledDateOrCron({
        date,
        dayOfWeek: dayOfWeek ?? 0,
        doesRepeat,
      });

      const feedIds = recipients.map((i) => i.id);
      if (doesRepeat) {
        await createScheduledWorkflowItem({
          workspaceId,
          workflowItemId: workflowItem.id,
          feedIds,
          scheduledCron: scheduledDateOrCron,
          timezone,
        });
      } else {
        await createScheduledWorkflowItem({
          workspaceId,
          workflowItemId: workflowItem.id,
          feedIds,
          scheduledDate: scheduledDateOrCron,
        });
      }
      resetValues();
    } catch (e) {
      log.error("Failed to publish scheduled message", e);
      setSubmitError("Failed to publish scheduled message. Please try again.");
    } finally {
      setDisabled(false);
    }
  };

  // Memoized handlers + activeChannels keep <SelectRecipients /> (React.memo) stable across
  // keystrokes in the text fields above. Without this, every keypress re-renders the entire
  // recipient list, which can be hundreds of MUI nodes (WEB-1740).
  const setRecipientIds = useCallback((values: string[]) => {
    setRecipients(values.map((id) => ({ id, title: null })));
  }, []);

  const toggleAllRecipients = useCallback((ids: string[]) => {
    setRecipients((prev) => {
      const hasSome = ids.some((feedId) => prev?.map((i) => i.id).includes(feedId));
      if (hasSome) {
        return prev?.filter((i) => !ids.includes(i.id)) ?? [];
      }
      return [...prev, ...ids.map((id) => ({ id, title: null }))];
    });
  }, []);

  const activeChannelIds = useMemo(() => recipients.map((i) => i.id), [recipients]);

  const tabs = modalTabs.map((tab) => ({
    ...tab,
    active: tab.key === activeTab,
  }));

  const DisabledBlock = ({ children }: { children: ReactNode }) => (
    <Box>
      <Box
        sx={{
          p: 2,
          width: "100%",
          borderRadius: "0.5rem",
          border: `solid 1px ${theme.palette.error.main}`,
          height: "100%",
          background: theme.palette.secondary.dark,
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "480px", position: "relative" }}>
      <Box sx={{ mb: 2 }}>
        <ButtonTabs tabs={tabs} setActiveTab={(key) => setActiveTab(key)} />
      </Box>

      <Collapse in={activeTab === "generate"}>
        <Box sx={{ position: "relative", height: boxHeight }}>
          {hasFile ? (
            <DisabledBlock>
              <Box>
                <Box sx={{ fontWeight: "bold" }}>Generate Audio Disabled</Box>
                <Box sx={{ mb: 1 }}>You can either have an attachment or generated audio, not both.</Box>
                <Box
                  onClick={() => setSelectedFile(null)}
                  component="button"
                  type="button"
                  sx={{
                    background: theme.palette.secondary.main,
                    px: 1,
                    py: 0.5,
                  }}
                  className="basic-button"
                >
                  Remove Attachment
                </Box>
              </Box>
            </DisabledBlock>
          ) : (
            <>
              <FullInput
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>Internal Name*</Typography>
                    <Typography sx={{ fontSize: "14px" }}>Only you will be able to see this name.</Typography>
                  </Box>
                }
                sx={{
                  position: "relative",
                  width: "100%",
                  p: 0,
                  border: "solid 1px transparent",
                  "label + &": { marginTop: "52px" },
                }}
                value={displayName}
                callback={(e) => setDisplayName(e.target.value)}
                error={displayName.length > internalNameCharLimit}
                helperText={maxDisplayNameHelper}
              />
              <TextMessageInput
                label="Enter text to generate audio"
                rows={6}
                error={!textValid}
                textInputValue={scheduledText}
                textInputHandler={(value: string) => setScheduledText(value)}
                validateTextHandler={(value: boolean) => setTextValid(value)}
              />
            </>
          )}
        </Box>
      </Collapse>

      <Collapse in={activeTab === "media"}>
        <Box sx={{ position: "relative", height: boxHeight }}>
          {hasText ? (
            <DisabledBlock>
              <Box>
                <Box sx={{ fontWeight: "bold" }}>Add Media Disabled</Box>
                <Box sx={{ mb: 1 }}>You can either have an attachment or generated audio, not both.</Box>
                <Box
                  onClick={() => setScheduledText("")}
                  component="button"
                  type="button"
                  sx={{
                    background: theme.palette.secondary.main,
                    px: 1,
                    py: 0.5,
                  }}
                  className="basic-button"
                >
                  Clear Generated Audio
                </Box>
              </Box>
            </DisabledBlock>
          ) : (
            <>
              <FullInput
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>Internal Name*</Typography>
                    <Typography sx={{ fontSize: "14px" }}>Only you will be able to see this name.</Typography>
                  </Box>
                }
                sx={{
                  position: "relative",
                  width: "100%",
                  p: 0,
                  border: "solid 1px transparent",
                  "label + &": { marginTop: "52px" },
                }}
                value={displayName}
                callback={(e) => setDisplayName(e.target.value)}
                error={displayName.length > internalNameCharLimit}
                helperText={maxDisplayNameHelper}
              />
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" component="label" disabled={isUploading || disabled}>
                  Choose file
                  <input
                    type="file"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setSelectedFile(f);
                    }}
                  />
                </Button>
                {selectedFile && (
                  <Typography sx={{ mt: 1, display: "block" }} variant="body2">
                    {selectedFile.name}
                  </Typography>
                )}
                {isUploading && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress variant="determinate" value={progressFraction * 100} />
                  </Box>
                )}
                {tusError && (
                  <Typography color="error" variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    {tusError.message}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Collapse>

      <Collapse in={activeTab === "date"}>
        {date && (
          <Box sx={{ height: boxHeight }}>
            <Box sx={{ display: "flex", gap: 2, mb: doesRepeat ? 3 : 0 }}>
              {!doesRepeat && (
                <Box sx={{ width: "50%" }}>
                  <ScheduleDateTimePicker
                    label="Publish Date"
                    type="date"
                    dateTime={date}
                    saving={disabled}
                    setInvalidTime={setInvalidTime}
                    setDateTime={(value, dow) => {
                      setDayOfWeek(doesRepeat ? dow : null);
                      setDate(new Date(value));
                    }}
                  />
                </Box>
              )}
              <Box sx={{ width: doesRepeat ? "100%" : "50%" }}>
                <ScheduleDateTimePicker
                  type="time"
                  label="Time"
                  dateTime={date}
                  saving={disabled}
                  setInvalidTime={setInvalidTime}
                  doesRepeat={doesRepeat}
                  setDateTime={(d) => {
                    setDate(new Date(d));
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Box>Does this message repeat?</Box>
              <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
                <label>
                  <Radio
                    checked={doesRepeat}
                    onChange={() => {
                      setDoesRepeat(true);
                      setDayOfWeek((d) => (d != null ? d : 1));
                    }}
                  />
                  <span>Custom Recurrence</span>
                </label>
                <label>
                  <Radio
                    checked={!doesRepeat}
                    onChange={() => {
                      setDoesRepeat(false);
                      setDayOfWeek(null);
                    }}
                  />
                  <span>Does Not Repeat</span>
                </label>
              </Box>
              <Box sx={{ display: doesRepeat ? "block" : "none" }}>
                <DaysOfWeekPicker
                  disabled={!doesRepeat}
                  activeIndex={dayOfWeek}
                  label="Repeat every"
                  selectedDays={doesRepeat && dayOfWeek != null ? [dayOfWeek] : []}
                  daySelectionHandler={(value: number[]) => {
                    let initialValue = value[0] || 1;
                    if (!initialValue || initialValue === -1) {
                      initialValue = 0;
                    }
                    setDayOfWeek(initialValue);
                    setDate(setDay(date, initialValue - 1));
                  }}
                  multiDay={false}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Collapse>

      <Collapse in={activeTab === "channel"}>
        <Box sx={{ height: boxHeight }}>
          <SelectRecipients
            activeChannels={activeChannelIds}
            setActiveIds={setRecipientIds}
            toggleAllIds={toggleAllRecipients}
            allowNonAliasChannels
          />
        </Box>
      </Collapse>

      <Box
        sx={{
          position: "sticky",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderRadius: "1rem",
          left: 0,
          bottom: 0,
          background: theme.palette.secondary.dark,
          pt: 1,
        }}
      >
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            type="button"
            onClick={() => (hasChanges ? setConfirmDiscard(true) : closeModal())}
            variant="outlined"
            disabled={disabled || isUploading}
          >
            Cancel
          </Button>
          <LoadingButton
            type="button"
            onClick={() => void save()}
            loading={disabled || isUploading}
            disabled={!canPublish || !hasChanges}
            variant={canPublish && hasChanges ? "contained" : "outlined"}
          >
            Publish
          </LoadingButton>
        </Box>
      </Box>

      <ConfirmDiscardChanges
        id="discard-scheduled-message-composer"
        visible={confirmDiscard}
        text="Are you sure you want to discard these changes?"
        denyCloseHandler={() => setConfirmDiscard(false)}
        confirmCloseHandler={() => {
          setConfirmDiscard(false);
          resetValues();
        }}
      />
    </Box>
  );
}
